import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowUpRight, Clock, MapPin, Users } from "lucide-react";
import { api } from "@/api";
import { useLang } from "@/i18n";

const CATEGORY_IMG = {
  corsi: "https://customer-assets-gfyr7b9c.emergentagent.net/job_water-sports-reserve/artifacts/vkx0o1a0_1000132194.jpg",
  escursioni: "https://customer-assets-gfyr7b9c.emergentagent.net/job_water-sports-reserve/artifacts/mak6mz1n_1000132412.jpg",
  natanti: "https://customer-assets-gfyr7b9c.emergentagent.net/job_water-sports-reserve/artifacts/j9ynhlfu_1000132193%20%281%29.jpg",
  vela: "https://customer-assets-gfyr7b9c.emergentagent.net/job_water-sports-reserve/artifacts/otb1dopj_760916310_27402174559484526_4844914775450722204_n.jpg",
};

function PriceLines({ s, t }) {
  const p = s.pricing;
  if (p.type === "group_tier") {
    const labels = ["1", "2", "3+"];
    return (
      <div className="space-y-1 font-mono text-sm">
        {p.tiers.map((tier, i) => (
          <div key={i} className="flex justify-between border-b border-lake-deep/8 pb-1">
            <span className="text-lake-deep/60">{labels[i]} {parseInt(labels[i]) === 1 || labels[i] === "1" ? "" : ""}{labels[i] === "1" ? "" : ""}{i === 0 ? "1 persona / person" : `${labels[i]} ${t.services.people}`}</span>
            <span className="font-semibold">€{tier.pp} {t.services.perPerson}</span>
          </div>
        ))}
      </div>
    );
  }
  if (p.type === "flat") {
    return (
      <div className="font-mono">
        <span className="text-2xl sm:text-3xl font-semibold tracking-tight">€{p.pp}</span>
        <span className="text-lake-deep/60 text-sm"> {t.services.perPerson}{p.period_it ? ` ${p.period_it === "al mese" ? t.services.month : ""}` : ""}</span>
      </div>
    );
  }
  return (
    <div className="flex gap-4 font-mono text-sm">
      {Object.entries(p.rates).map(([h, price]) => (
        <div key={h}>
          <div className="text-lake-deep/60">{h}h</div>
          <div className="font-semibold text-base">€{price}</div>
        </div>
      ))}
    </div>
  );
}

export default function Services() {
  const { lang, t } = useLang();
  const [services, setServices] = useState([]);
  const [tab, setTab] = useState("corsi");

  useEffect(() => {
    api.get("/services").then((r) => setServices(r.data)).catch(() => {});
  }, []);

  const filtered = services.filter((s) => s.category === tab);
  const tabs = [
    { id: "corsi", label: t.services.tabCorsi, testid: "service-tab-courses" },
    { id: "escursioni", label: t.services.tabTour, testid: "service-tab-tours" },
    { id: "natanti", label: t.services.tabRental, testid: "service-tab-rentals" },
    { id: "vela", label: t.services.tabVela, testid: "service-tab-sailing" },
  ];

  return (
    <section id="esperienze" data-testid="services-section" className="bg-travertine py-24 sm:py-32 grain relative">
      <div className="max-w-7xl mx-auto px-5 sm:px-8">
        <motion.div
          initial={{ opacity: 0, y: 40 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-80px" }}
          transition={{ duration: 0.9, ease: [0.16, 1, 0.3, 1] }}
          className="max-w-2xl"
        >
          <p className="font-mono text-xs uppercase tracking-[0.3em] text-teal mb-4">{t.services.eyebrow}</p>
          <h2 className="font-serif text-3xl sm:text-5xl tracking-tight leading-[1.08] text-lake-deep">
            {t.services.title}
          </h2>
          <p className="mt-4 text-lake-deep/65 text-base sm:text-lg leading-relaxed">{t.services.sub}</p>
        </motion.div>

        <div className="mt-10 flex gap-2 flex-wrap" role="tablist">
          {tabs.map((tb) => (
            <button
              key={tb.id}
              data-testid={tb.testid}
              role="tab"
              aria-selected={tab === tb.id}
              onClick={() => setTab(tb.id)}
              className={`px-5 py-2.5 rounded-full text-sm font-medium transition-colors duration-300 border ${
                tab === tb.id
                  ? "bg-lake-deep text-glacier border-lake-deep"
                  : "bg-transparent text-lake-deep/70 border-lake-deep/20 hover:border-lake-deep/50"
              }`}
            >
              {tb.label}
            </button>
          ))}
        </div>

        <div className="mt-12 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {filtered.map((s, i) => (
            <motion.article
              key={s.id}
              data-testid={`service-card-${s.id}`}
              initial={{ opacity: 0, y: 50 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-60px" }}
              transition={{ duration: 0.8, delay: i * 0.08, ease: [0.16, 1, 0.3, 1] }}
              className="group bg-sand border border-lake-deep/10 rounded-lg overflow-hidden hover:-translate-y-1.5 hover:shadow-[0_24px_50px_-20px_rgba(8,27,38,0.35)] transition-[transform,box-shadow] duration-500 flex flex-col"
            >
              <div className="relative aspect-[16/10] overflow-hidden">
                <img
                  src={CATEGORY_IMG[s.category]}
                  alt={lang === "it" ? s.title_it : s.title_en}
                  loading="lazy"
                  className="w-full h-full object-cover grayscale-[35%] group-hover:grayscale-0 group-hover:scale-105 transition-[filter,transform] duration-700"
                />
                <span className="absolute top-3 left-3 font-mono text-[10px] tracking-[0.2em] bg-lake-deep/70 text-glacier backdrop-blur px-2.5 py-1 rounded-full">
                  N.{String(i + 1).padStart(2, "0")}
                </span>
                {s.badge_it && (
                  <span className="absolute top-3 right-3 font-mono text-[10px] tracking-[0.15em] bg-gold text-lake-deep px-2.5 py-1 rounded-full font-semibold">
                    {lang === "it" ? s.badge_it : s.badge_en}
                  </span>
                )}
              </div>
              <div className="p-6 flex flex-col flex-1">
                <h3 className="font-serif text-xl sm:text-2xl font-semibold text-lake-deep tracking-tight">
                  {lang === "it" ? s.title_it : s.title_en}
                </h3>
                <div className="mt-2 flex flex-wrap items-center gap-x-4 gap-y-1 font-mono text-[11px] uppercase tracking-[0.12em] text-teal">
                  <span className="flex items-center gap-1.5">
                    <Clock className="w-3.5 h-3.5" />
                    {lang === "it" ? s.duration_it : s.duration_en}
                  </span>
                  {s.min_participants > 1 && (
                    <span className="flex items-center gap-1.5">
                      <Users className="w-3.5 h-3.5" />
                      {t.services.min} {s.min_participants}
                    </span>
                  )}
                </div>
                {s.route_it && (
                  <p className="mt-3 flex items-start gap-1.5 text-sm text-lake-deep/60">
                    <MapPin className="w-4 h-4 mt-0.5 shrink-0 text-terracotta" />
                    {lang === "it" ? s.route_it : s.route_en}
                  </p>
                )}
                <p className="mt-3 text-sm text-lake-deep/65 leading-relaxed flex-1">
                  {lang === "it" ? s.desc_it : s.desc_en}
                </p>
                <div className="mt-5">
                  <PriceLines s={s} t={t} />
                  {s.pricing.tessera ? (
                    <p className="mt-2 font-mono text-[11px] text-terracotta">{t.services.tessera}</p>
                  ) : null}
                </div>
                <Link
                  data-testid={`book-service-${s.id}`}
                  to={`/prenota?service=${s.id}`}
                  className="mt-6 inline-flex items-center justify-between border border-lake-deep/15 rounded-full px-5 py-3 text-sm font-semibold text-lake-deep group-hover:bg-lake-deep group-hover:text-glacier transition-colors duration-300"
                >
                  {t.services.book}
                  <ArrowUpRight className="w-4 h-4" />
                </Link>
              </div>
            </motion.article>
          ))}
        </div>
      </div>
    </section>
  );
}
